<template>
  <nav>
    <VTabsV2
      :tabs="tabs"
      :active-tab="activeTab"
      @update="onUpdateActiveTab"
    />

    <div class="header-actions">
      <VButton theme="link" @click="showGuide">
        <div ref="guide-toggle-btn">Обучение</div>
        <template #icon>
          <VIcon material="info" theme="outlined"/>
        </template>
      </VButton>

      <VConnectionCanary/>

      <VReloadButton title="Обновить список тендеров" @renew-data="onForceReload"/>

      <TendersFilter
        :tenders-page-filter="tendersPageFilter"
        @tenders-page-filter:updated="onFiltersUpdated"
      />
    </div>
  </nav>

  <TendersRegistry
    ref="registry"
    :params="registryParams"
    :show-status-filter="false"
    :hidden-columns="activeTabDeclaration.hiddenColumns"
    :is-live="activeTabDeclaration.isLive"
  />

  <TenderGuidePopup
    ref="guidePopup"
    :guide-on="guideOn"
    :storage-key="PREFERENCES_TENDER_GUIDE_STORAGE_KEY"
    @close="closeGuide"
  />
</template>

<style lang="scss" src="./TendersFlow.scss"/>

<script lang="ts">
import Core              from "@/core/Core";

import TendersService    from '@/domains/Tenders/TendersService';
import TendersPageFilter from '@/domains/Tenders/TendersPageFilter';

import TendersRegistry   from '@/domains/Tenders/components/TendersRegistry/TendersRegistry.vue';
import TendersFilter     from '@/domains/Tenders/components/TendersFilter/TendersFilter.vue';
import TenderGuidePopup  from '@/domains/Tenders/components/TenderGuidePopup/TenderGuidePopup.vue';
import VReloadButton     from '@/lib/components/VReloadButton/VReloadButton.vue';

// TabElement нужен как значение (TabElement.from), а не только как тип
import TabElement from '@/lib/components/VTabsV2/TabElement';

const tenderGuideQuery = 'tender-guide';
const PREFERENCES_TENDER_GUIDE_STORAGE_KEY = 'tenderGuideRegistry';

/** Ключ вкладки в хэше маршрута */
const TAB_HASH_KEY = 'tenders-tab';

/** Дефолтная вкладка при переходе на страницу тендеров */
const DEFAULT_TAB_ID = 'active';

type TenderFlowTab = {
  /** id вкладки, он же значение в хэше маршрута */
  id: string;
  name: string;
  /** роль, которой видна вкладка; null — видна обеим */
  role: 'buyer' | 'seller' | null;
  /** id колонок реестра, которые на этой вкладке не показываются.
   *  Покупатель скрыт на всех вкладках: в воронке партнёр смотрит свои
   *  тендеры, колонка нужна только вне её. */
  hiddenColumns: string[];
  /** нужен ли реестру лонг-поллинг на этой вкладке; содержимое остальных вкладок не меняется само по себе */
  isLive: boolean;
  /** параметры запроса реестра для этой вкладки */
  params: NonNullable<Parameters<typeof TendersService.getListPaginated>[0]>;
};

/**
 * Вкладки воронки тендеров — единственный источник правды.
 *
 * Раньше это были две таблицы в TendersService: FLOW_TABS (id + name + роли)
 * и switch в getRequestParamsByTab (id + параметры запроса). Их приходилось
 * держать синхронными руками. Вкладки не переиспользуются вне воронки,
 * поэтому в сервисе им делать нечего.
 *
 * Модель параметров смешанная и это осознанно: три вкладки — фазы торгов
 * и живут на status, три — срезы по другим признакам (избранное, удалённые,
 * результат участия) и живут на флагах. Статус на бэке приоритетнее флагов,
 * поэтому в одной вкладке они не смешиваются.
 *
 * Поле hidden было у всех записей false, поэтому в декларации его нет —
 * в TabElement.from передаётся литералом.
 */
const FLOW_TABS: TenderFlowTab[] = [
  {
    id:            'bookmarked',
    name:          'Избранные',
    role:          null,
    hiddenColumns: ['hasOrder', 'buyer'],
    isLive:        true,
    params:        { isBookmarked: true },
  },
  {
    id:            'active',
    name:          'Активные',
    role:          null,
    hiddenColumns: ['hasOrder', 'buyer'],
    isLive:        true,
    /** Идут торги + завершившиеся в пределах 24 часов — окно считает бэк */
    params:        { status: TendersService.STATUS.ACTUAL },
  },
  {
    id:            'isFinished',
    name:          'Завершённые',
    role:          null,
    hiddenColumns: ['buyer'],
    isLive:        false,
    params:        { status: TendersService.STATUS.READY_FOR_ORDER },
  },
  {
    id:            'hasOrder',
    name:          'Создан заказ',
    role:          null,
    hiddenColumns: ['buyer'],
    isLive:        false,
    params:        { status: TendersService.STATUS.ORDER_CREATED },
  },
  {
    id:            'lost',
    name:          'Не выиграл',
    role:          'seller',
    hiddenColumns: ['hasOrder', 'buyer'],
    isLive:        false,
    params:        {
      isFinished:                     true,
      isWonByCurrentPartner:          false,
      isParticipatedByCurrentPartner: true,
    },
  },
  {
    id:            'isDeleted',
    name:          'Удалённые',
    role:          'buyer',
    hiddenColumns: ['hasOrder', 'buyer'],
    isLive:        false,
    params:        { isDeleted: true },
  },
];

export default defineComponent({
  name: 'TendersPage',

  components: {
    TendersRegistry,
    TendersFilter,
    TenderGuidePopup,
    VReloadButton,
  },

  setup() {
    return {
      PREFERENCES_TENDER_GUIDE_STORAGE_KEY,
    };
  },

  data() {
    return {
      tendersPageFilter: TendersPageFilter.fromLocalStorage(),
    };
  },

  computed: {
    /** Декларации вкладок, доступные текущей роли */
    tabDeclarations(): TenderFlowTab[] {
      let role: 'buyer' | 'seller';

      if (Core.currentPartner.isSeller) {
        role = 'seller';
      } else {
        role = 'buyer';
      }

      return FLOW_TABS.filter(declaration => declaration.role === null || declaration.role === role);
    },

    tabs(): TabElement[] {
      return this.tabDeclarations.map(declaration => TabElement.from(declaration.id, declaration.name, null, false));
    },

    /** id активной вкладки - читается из хэша `#tenders-tab=...` или дефолт */
    activeTabId(): string {
      const hashParams = new URLSearchParams(this.$route.hash.slice(1));
      return hashParams.get(TAB_HASH_KEY) || DEFAULT_TAB_ID;
    },

    /** Декларация активной вкладки - отсюда берутся параметры запроса */
    activeTabDeclaration(): TenderFlowTab {
      const declaration = this.tabDeclarations.find(item => item.id === this.activeTabId);

      if (declaration) {
        return declaration;
      } else {
        return this.tabDeclarations[0];
      }
    },

    /** Активная вкладка как объект - нужна для VTabsV2 */
    activeTab(): TabElement {
      const tab = this.tabs.find(item => item.id === this.activeTabDeclaration.id);

      if (tab) {
        return tab;
      } else {
        return this.tabs[0];
      }
    },

    registryParams(): Parameters<typeof TendersService.getListPaginated>[0] {
      return {
        ...this.tendersPageFilter.getRequestParams(),
        ...this.activeTabDeclaration.params,
      };
    },

    guideOn(): boolean {
      return this.$route.query?.[tenderGuideQuery] === 'true';
    },
  },

  created() {
    this.initGuideFromQuery();
  },

  methods: {
    onFiltersUpdated(newFilter: TendersPageFilter) {
      this.tendersPageFilter = newFilter;
    },

    onForceReload() {
      (this.$refs.registry as any)?.refresh();
    },

    /** Клик по табу в VTabsV2 (state-mode) - пишем новый id в хэш */
    onUpdateActiveTab(tab: TabElement) {
      const newTabId = tab.id;

      if (this.activeTabId === newTabId) return;

      const hashParams = new URLSearchParams(this.$route.hash.slice(1));
      hashParams.set(TAB_HASH_KEY, newTabId);

      this.$router.push({
        query: this.$route.query,
        hash:  `#${hashParams.toString()}`,
      });
    },

    // ==================== Guide ====================

    showGuide() {
      this.$router.replace({
        query: { ...this.$route.query, [tenderGuideQuery]: true },
        hash:  this.$route.hash,
      });
    },

    closeGuide() {
      this.$router.replace({
        query: { ...this.$route.query, [tenderGuideQuery]: undefined },
        hash:  this.$route.hash,
      });
    },

    initGuideFromQuery() {
      if (this.$route.query?.[tenderGuideQuery] === true) {
        this.showGuide();
        return;
      }

      const storageItem = localStorage.getItem(PREFERENCES_TENDER_GUIDE_STORAGE_KEY);

      if (typeof storageItem !== 'string' || storageItem.length === 0) {
        localStorage.setItem(PREFERENCES_TENDER_GUIDE_STORAGE_KEY, JSON.stringify(true));
        this.showGuide();
      } else if (JSON.parse(storageItem)) {
        this.showGuide();
      }
    },
  },
});
</script>
